import  React  from 'react';
import type * as C from './IComponent';
import '@/assets/styles/common.less';
import '@/assets/styles/ui-component.less';
import CommonBox from '@/basic/CommonBox/CommonBox';

import classNames from 'classnames';
import useScreenWidth from '@/utils/hooks/useScreenWidth';
import { ReoIcon } from '@reolink-deps/reolink-web_components.basic';
import FixedTopCategoryNav from './components/FixedTopCategoryNav';
import CompBasicTitleWithDesc from '@/basic/TitleWithDesc';
import styles from './index.module.less';

const CATEGORY_ITEM_WIDTH = 160;
const CATEGORY_CONTENT_DEFAULT_WIDTH = 1180;
const PROGRESS_BAR_CONTAINER_WIDTH = 35;
const PROGRESS_BAR_WIDTH = 13;
const maxBarTranslate = PROGRESS_BAR_CONTAINER_WIDTH - PROGRESS_BAR_WIDTH;

const CommonCategoryNav: React.FC<C.IProps> = (props) => {
    const screenWidth = useScreenWidth<number>();
    const [transXQty, setTransXQty] = React.useState<number>(0);
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState<boolean>(false);
    const [scrollPercent, setScrollPercent] = React.useState<number>(0);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const categoryListContentRef = React.useRef<HTMLUListElement>(null);
    const progressBarRef = React.useRef<HTMLDivElement>(null);
    const rafId = React.useRef<number>();

    const handleScroll = React.useCallback(() => {
        if (rafId.current) {
            cancelAnimationFrame(rafId.current);
        }
        rafId.current = requestAnimationFrame(() => {
            const content = contentRef.current;
            const bar = progressBarRef.current;
            if (!content || !bar) return;
            const { scrollLeft, scrollWidth, clientWidth } = content;
            let percent = 0;
            if (scrollWidth > clientWidth) {
                percent = scrollLeft / (scrollWidth - clientWidth);
                if (percent < 0) percent = 0;
                if (percent > 1) percent = 1;
            }
            bar.style.transform = `translateX(${percent * maxBarTranslate}px)`;
        });
    }, [maxBarTranslate]);

    const updateContentTranslateX = (qty: number): void => {
        if (!categoryListContentRef.current || !contentRef.current) return;
        categoryListContentRef.current.style.transform = `translateX(${-qty * CATEGORY_ITEM_WIDTH}px)`;
        setTransXQty(qty);
        const content = contentRef.current;
        const totalWidth = props.categoryList.length * CATEGORY_ITEM_WIDTH;
        const visibleWidth = content.clientWidth ?? CATEGORY_CONTENT_DEFAULT_WIDTH;
        const maxTransX = Math.max(0, totalWidth - visibleWidth);
        const currentTransX = qty * CATEGORY_ITEM_WIDTH;
        setNextButtonDisabled((props.categoryList.length - qty) * CATEGORY_ITEM_WIDTH <= visibleWidth);
        const percent = maxTransX === 0 ? 0 : currentTransX / maxTransX;
        setScrollPercent(percent);
        if (progressBarRef.current) {
            progressBarRef.current.style.transform = `translateX(${percent * maxBarTranslate}px)`;
        }
    };

    const handlePrevClick = (): void => {
        if (transXQty <= 0) return;
        updateContentTranslateX(transXQty - 1);
    };
    const handleNextClick = (): void => {
        if (nextButtonDisabled) return;
        updateContentTranslateX(transXQty + 1);
    };

    React.useEffect(() => {
        updateContentTranslateX(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenWidth, props.categoryList.length]);

    React.useEffect(() => {
        return () => {
            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
            }
        };
    }, []);

    return (
        <CommonBox style={ props.style }>
            <div className={ styles['component-container'] }>
                <CompBasicTitleWithDesc
                    title={ props.title }
                    className={ styles['title-content'] }
                    titleClassName={ styles['title'] }
                />
                <div
                    onClick={ handlePrevClick }
                    className={ classNames(styles['switch-button'], styles['pre-button'], {
                        [styles['switch-button-disabled']]: transXQty <= 0
                    }) }
                >
                    <ReoIcon
                        name={ 'icon-icon_arrow-left' }
                        className={ styles['switch-button-icon'] }
                    />
                </div>
                <div
                    className={ styles['content'] }
                    ref={ contentRef }
                    onScroll={ handleScroll }
                >
                    <ul
                        className={ styles['category-list'] }
                        ref={ categoryListContentRef }
                    >
                        { props.categoryList.map((item) => (
                            <li
                                key={ item.id }
                                className={ styles['category-item'] }
                            >
                                <a
                                    className={ styles['category-item-content'] }
                                    href={ item.link }
                                >
                                    <div className={ styles['category-item-img'] }>
                                        <img
                                            src={ item.icon }
                                            alt={ item.name }
                                        />
                                    </div>
                                    <div className={ styles['category-item-text'] }>
                                        <p>{item.name}</p>
                                    </div>
                                </a>
                            </li>
                        )) }
                    </ul>
                </div>
                <div className={ styles['scroll-progress-bar-container'] }>
                    <div
                        className={ styles['scroll-progress-bar'] }
                        ref={ progressBarRef }
                    />
                </div>
                <div
                    onClick={ handleNextClick }
                    className={ classNames(
                        styles['switch-button'],
                        styles['next-button'],
                        {
                            [styles['switch-button-disabled']]: nextButtonDisabled
                        }
                    ) }
                >
                    <ReoIcon
                        name={ 'icon-icon_arrow-right' }
                        className={ styles['switch-button-icon'] }
                    />
                </div>
            </div>
        </CommonBox>
    );
};

const CategoryNav: React.FC<C.IProps> = (props) => {
    return (
        props.firstScreenHideAndScroll ? (
            <FixedTopCategoryNav
                { ...props }
            />
        ) : (
            <CommonCategoryNav
                { ...props }
            />
        )
    );
};

export default CategoryNav;
